import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { getBestAuthToken } from '../../utils/authHeaders';

/**
 * Decode a JWT payload without jose dependency.
 * Returns null on any failure.
 */
function decodeRoleFromToken(): string | null {
  try {
    const token = getBestAuthToken();
    if (!token) return null;
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    const payload = JSON.parse(atob(parts[1]));
    return payload.role ?? null;
  } catch {
    return null;
  }
}

/**
 * Default authenticated landing route with role-aware first destination.
 *
 * Reads the role from two sources (in order):
 * 1. React auth context (authoritative after re-render)
 * 2. JWT payload (available immediately after login, before state commits)
 *
 * This dual-read prevents the race condition where login() sets user state
 * then navigate('/') fires before React commits the state update.
 */
export const NavigateAppDefault: React.FC = () => {
  const { user } = useAuth();

  // Prefer context user; fall back to JWT-decoded role for the login→redirect race
  const role = user?.role ?? decodeRoleFromToken();

  if (role === 'external_collaborator') {
    const firstCampaignId = user?.collaborator_campaign_ids?.[0];
    if (firstCampaignId) {
      return <Navigate to={`/campaign/${firstCampaignId}/workflow/1`} replace />;
    }
    return <Navigate to="/campaigns" replace />;
  }

  if (role === 'partner_user') {
    return <Navigate to="/partner/dashboard" replace />;
  }

  if (role === 'agency_operator') {
    return <Navigate to="/campaigns" replace />;
  }

  return <Navigate to="/dashboard" replace />;
};
